import React, { useState, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Save, ArrowLeft } from "lucide-react";
import { adminApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";

export default function AdminAnnouncementNew() {
  const navigate = useNavigate();
  const params = useParams();
  const { token } = useAuthStore();
  const announcementId = params.id ? parseInt(params.id) : null;
  const isEdit = announcementId !== null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    message: "",
    link_url: "",
    link_text: "",
    background_color: "#000000",
    text_color: "#ffffff",
    is_active: true,
    display_order: 0,
  });

  useEffect(() => {
    if (isEdit && token && announcementId) {
      fetchAnnouncement();
    }
  }, [isEdit, token, announcementId]);

  const fetchAnnouncement = async () => {
    if (!token || !announcementId) return;
    try {
      const response = await adminApi.getAnnouncement(announcementId, token);
      const announcement = response.announcement || response.data?.announcement || response.data || response;
      setFormData({
        message: announcement.message || "",
        link_url: announcement.link_url || "",
        link_text: announcement.link_text || "",
        background_color: announcement.background_color || "#000000",
        text_color: announcement.text_color || "#ffffff",
        is_active: announcement.is_active !== undefined ? announcement.is_active : true,
        display_order: announcement.display_order || 0,
      });
    } catch (error: any) {
      console.error("Error fetching announcement:", error);
      toast.error(error.response?.data?.detail || "Failed to load announcement");
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast.error("Authentication required");
      return;
    }

    if (!formData.message.trim()) {
      toast.error("Please enter an announcement message");
      return;
    }

    setLoading(true);

    try {
      const announcementData = {
        message: formData.message.trim(),
        link_url: formData.link_url.trim() || undefined,
        link_text: formData.link_text.trim() || undefined,
        background_color: formData.background_color || undefined,
        text_color: formData.text_color || undefined,
        is_active: formData.is_active,
        display_order: parseInt(formData.display_order.toString()) || 0,
      };

      if (isEdit && announcementId) {
        await adminApi.updateAnnouncement(announcementId, announcementData, token);
        toast.success("Announcement updated successfully!");
      } else {
        await adminApi.createAnnouncement(announcementData, token);
        toast.success("Announcement created successfully!");
      }

      navigate("/admin/announcements");
    } catch (error: any) {
      console.error("Error saving announcement:", error);
      toast.error(error.response?.data?.detail || `Failed to ${isEdit ? "update" : "create"} announcement`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4">
        <Link
          to="/admin/announcements"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-aarsa-sage-600 mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Announcements
        </Link>
        <h1 className="text-xl font-bold font-playfair mb-0.5">
          {isEdit ? "Edit Announcement" : "Create New Announcement"}
        </h1>
        <p className="text-xs text-gray-500">Manage website announcement bar content</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-3">
          {/* Announcement Details */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
              <div className="flex items-center gap-2.5">
                <h2 className="text-sm font-bold text-gray-900">Announcement Details</h2>
                <span className="text-xs text-red-500 ml-auto">* Required</span>
              </div>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                  placeholder="Enter announcement message..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Link URL
                  </label>
                  <input
                    type="url"
                    name="link_url"
                    value={formData.link_url}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="https://example.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Link Text
                  </label>
                  <input
                    type="text"
                    name="link_text"
                    value={formData.link_text}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="Learn More"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Background Color
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      name="background_color"
                      value={formData.background_color}
                      onChange={handleInputChange}
                      className="w-12 h-10 border border-gray-200 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      name="background_color"
                      value={formData.background_color}
                      onChange={handleInputChange}
                      className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                      placeholder="#000000 or bg-black"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Hex color (e.g., #000000) or Tailwind class (e.g., bg-black)</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Text Color
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      name="text_color"
                      value={formData.text_color}
                      onChange={handleInputChange}
                      className="w-12 h-10 border border-gray-200 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      name="text_color"
                      value={formData.text_color}
                      onChange={handleInputChange}
                      className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                      placeholder="#ffffff or text-white"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Hex color (e.g., #ffffff) or Tailwind class (e.g., text-white)</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Display Order
                  </label>
                  <input
                    type="number"
                    name="display_order"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                    min="0"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Status
                  </label>
                  <div className="flex items-center gap-3 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-aarsa-sage-600 border-gray-300 rounded focus:ring-aarsa-sage-500"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Only active announcements are displayed</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Preview */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
              <h2 className="text-sm font-bold text-gray-900">Preview</h2>
            </div>
            <div className="p-4">
              <div
                className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded"
                style={{
                  backgroundColor: formData.background_color?.startsWith("#") ? formData.background_color : undefined,
                  color: formData.text_color?.startsWith("#") ? formData.text_color : undefined,
                }}
              >
                <span>{formData.message || "Announcement message preview..."}</span>
                {formData.link_url && formData.link_text && (
                  <a href={formData.link_url} className="underline font-semibold">
                    {formData.link_text}
                  </a>
                )}
              </div>
            </div>
          </motion.div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-2 pb-2">
            <Link
              to="/admin/announcements"
              className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {isEdit ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEdit ? "Update Announcement" : "Create Announcement"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
